import axios from 'axios'
import { API_INCIDENCIAS } from '@/constants'

const base = () => API_INCIDENCIAS.baseUrl

export default {
  // ─── Listado ────────────────────────────────────────────────────────────────

  getIncidencias({ commit, state }, { page = 1, perPage = 20 } = {}) {
    commit('SET_LOADING', true)
    return axios.get(base() + API_INCIDENCIAS.lista, {
      params: { page, per_page: perPage, ...state.filtros },
    })
      .then((res) => commit('SET_INCIDENCIAS', res.data))
      .catch((err) => { throw err })
      .finally(() => commit('SET_LOADING', false))
  },

  getKpis({ commit }) {
    return axios.get(base() + API_INCIDENCIAS.kpis)
      .then((res) => commit('SET_KPIS', res.data))
  },

  getArbolServicios({ commit, state }) {
    if (state.arbolServicios) return Promise.resolve()
    return axios.get(base() + API_INCIDENCIAS.arbolServicios)
      .then((res) => commit('SET_ARBOL', res.data.arbol))
  },

  getPlantillas({ commit }) {
    return axios.get(base() + API_INCIDENCIAS.plantillas)
      .then((res) => commit('SET_PLANTILLAS', res.data.plantillas))
  },

  // ─── Pickers "englobar" clientes afectados ────────────────────────────────────

  buscarServidoresIncidencia(_, q = '') {
    return axios.get(base() + API_INCIDENCIAS.buscarServidores, { params: { q } })
      .then((res) => res.data.servidores || [])
  },

  buscarCategoriasIncidencia(_, q = '') {
    return axios.get(base() + API_INCIDENCIAS.buscarCategorias, { params: { q } })
      .then((res) => res.data.categorias || [])
  },

  estimarAlcanceIncidencia(_, { clienteServicioIds = [], servidorIds = [], categoriaIds = [] }) {
    return axios.post(base() + API_INCIDENCIAS.estimarAlcance, {
      cliente_servicio_ids: clienteServicioIds,
      servidor_ids:         servidorIds,
      categoria_ids:        categoriaIds,
    }).then((res) => res.data)
  },

  getUsuariosResponsables({ commit }) {
    // Reutiliza el endpoint existente de usuarios del sistema
    return axios.get(base() + '/api/usuarios/get')
      .then((res) => commit('SET_USUARIOS', res.data.usuarios || []))
  },

  // ─── CRUD ───────────────────────────────────────────────────────────────────

  // Busca incidencias (activas o no) para asociar un ticket desde la vista del ticket.
  // No muta el listado global del módulo de incidencias.
  buscarIncidencias(_, q = '') {
    return axios.get(base() + API_INCIDENCIAS.lista, { params: { search: q, per_page: 20 } })
      .then((res) => res.data?.data || [])
  },

  crearIncidencia({ dispatch }, payload) {
    return axios.post(base() + API_INCIDENCIAS.lista, payload)
      .then((res) => {
        dispatch('getIncidencias')
        dispatch('getKpis')
        return res.data.incidencia
      })
  },

  actualizarIncidencia({ commit }, { id, payload }) {
    return axios.put(base() + API_INCIDENCIAS.detalle + id, payload)
      .then((res) => {
        commit('UPDATE_INCIDENCIA', res.data.incidencia)
        return res.data.incidencia
      })
  },

  // Marca un nodo afectado (servicio/servidor/categoría) como resuelto o lo reactiva
  cambiarEstadoNodo(_, { id, nodoId, estado }) {
    return axios.patch(base() + API_INCIDENCIAS.detalle + id + '/servicios-afectados/' + nodoId + '/estado', { estado })
      .then((res) => res.data.nodo)
  },

  cambiarEstado({ commit }, { id, estado, contenido }) {
    return axios.patch(base() + API_INCIDENCIAS.detalle + id + '/estado', {
      estado,
      contenido_seguimiento: contenido,
    })
      .then((res) => {
        commit('UPDATE_INCIDENCIA', res.data.incidencia)
        return res.data.incidencia
      })
  },

  eliminarIncidencia({ dispatch }, id) {
    return axios.delete(base() + API_INCIDENCIAS.detalle + id)
      .then(() => dispatch('getIncidencias'))
  },

  // ─── Detalle ────────────────────────────────────────────────────────────────

  getDetalle({ commit }, id) {
    commit('SET_LOADING_DETALLE', true)
    return axios.get(base() + API_INCIDENCIAS.detalle + id)
      .then((res) => commit('SET_DETALLE', res.data.incidencia))
      .finally(() => commit('SET_LOADING_DETALLE', false))
  },

  // Trae una incidencia completa sin mutar el estado global (para prellenar el modal de edición)
  fetchIncidencia(_, id) {
    return axios.get(base() + API_INCIDENCIAS.detalle + id)
      .then((res) => res.data.incidencia)
  },

  // ─── Tickets asociados ──────────────────────────────────────────────────────

  getTicketsAsociados({ commit }, { id, page = 1, filtros = {} }) {
    return axios.get(base() + API_INCIDENCIAS.detalle + id + '/tickets', {
      params: { page, ...filtros },
    }).then((res) => commit('SET_TICKETS', res.data))
  },

  asociarTickets(_, { id, ticketIds, visibilidad = 'solo_incidencia' }) {
    return axios.post(base() + API_INCIDENCIAS.detalle + id + '/tickets', {
      ticket_ids: ticketIds,
      visibilidad,
    })
  },

  desasociarTicket(_, { id, ticketId }) {
    return axios.delete(base() + API_INCIDENCIAS.detalle + id + '/tickets/' + ticketId)
  },

  generarTicketsLote(_, { id, payload }) {
    return axios.post(base() + API_INCIDENCIAS.detalle + id + '/tickets/generar-lote', payload)
  },

  buscarTickets(_, q) {
    return axios.get(base() + API_INCIDENCIAS.buscarTickets, { params: { q } })
      .then((res) => res.data.tickets)
  },

  exportarTickets(_, { id, format = 'csv' }) {
    return axios.get(base() + API_INCIDENCIAS.detalle + id + '/tickets/exportar', {
      params: { format },
      responseType: 'blob',
    })
  },

  // ─── Seguimientos ───────────────────────────────────────────────────────────

  getSeguimientos({ commit }, { id, page = 1 }) {
    return axios.get(base() + API_INCIDENCIAS.detalle + id + '/seguimientos', {
      params: { page },
    }).then((res) => commit('SET_SEGUIMIENTOS', res.data))
  },

  agregarSeguimiento({ dispatch }, { id, payload }) {
    return axios.post(base() + API_INCIDENCIAS.detalle + id + '/seguimientos', payload)
      .then(() => dispatch('getSeguimientos', { id }))
  },

  // ─── Comunicaciones ─────────────────────────────────────────────────────────

  getComunicaciones({ commit }, { id, page = 1 }) {
    return axios.get(base() + API_INCIDENCIAS.detalle + id + '/comunicaciones', {
      params: { page },
    }).then((res) => commit('SET_COMUNICACIONES', res.data))
  },

  enviarComunicacion({ dispatch }, { id, payload }) {
    return axios.post(base() + API_INCIDENCIAS.detalle + id + '/comunicaciones', payload)
      .then((res) => {
        dispatch('getComunicaciones', { id })
        dispatch('getDetalle', id)
        return res.data
      })
  },

  getEnviosComunicacion(_, { id, comunicacionId, page = 1 }) {
    return axios.get(
      base() + API_INCIDENCIAS.detalle + id + '/comunicaciones/' + comunicacionId + '/envios',
      { params: { page } }
    ).then((res) => res.data)
  },

  // ─── Auditoría ──────────────────────────────────────────────────────────────

  getAuditoria({ commit }, { id, page = 1, accion = null }) {
    return axios.get(base() + API_INCIDENCIAS.detalle + id + '/auditoria', {
      params: { page, accion },
    }).then((res) => commit('SET_AUDITORIA', res.data))
  },

  exportarAuditoria(_, { id, format = 'csv' }) {
    return axios.get(base() + API_INCIDENCIAS.detalle + id + '/auditoria/exportar', {
      params: { format },
      responseType: 'blob',
    })
  },

  // ─── Inventario Ansible ───────────────────────────────────────────────────────

  getInventarioAnsible(_, { id, soloCpanel = true }) {
    return axios.get(base() + API_INCIDENCIAS.detalle + id + '/servicios-afectados/inventario-ansible', {
      params: { solo_cpanel: soloCpanel ? 1 : 0 },
    }).then((res) => res.data)
  },
}
